import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL, apiFetch } from '../config/api';

/**
 * Lightweight comparison of two drone arrays.
 * Only checks fields that affect UI rendering to avoid unnecessary re-renders.
 */
function dronesEqual(prev, next) {
    if (prev.length !== next.length) return false;
    for (let i = 0; i < prev.length; i++) {
        const a = prev[i], b = next[i];
        if (a.id !== b.id ||
            a.status !== b.status ||
            a.onboardOnline !== b.onboardOnline ||
            a.name !== b.name ||
            a.battery !== b.battery ||
            a.connectionString !== b.connectionString ||
            a.type !== b.type ||
            a.vehicleModel !== b.vehicleModel) {
            return false;
        }
    }
    return true;
}

export const DRONE_ORDER_KEY = 'utms_drone_order';

export function applySavedDroneOrder(droneList) {
    if (!Array.isArray(droneList) || droneList.length === 0) return droneList || [];
    try {
        const raw = localStorage.getItem(DRONE_ORDER_KEY);
        if (!raw) return droneList;
        const savedOrder = JSON.parse(raw);
        if (!Array.isArray(savedOrder) || savedOrder.length === 0) return droneList;
        const map = new Map(droneList.map(d => [d.id, d]));
        const sorted = [];
        for (const id of savedOrder) {
            if (map.has(id)) {
                sorted.push(map.get(id));
                map.delete(id);
            }
        }
        for (const remaining of map.values()) {
            sorted.push(remaining);
        }
        return sorted;
    } catch {
        return droneList;
    }
}

/**
 * Custom hook for managing drone list and selection
 * @param {string} initialDroneId - Initial selected drone ID
 * @returns {Object} drones state and handlers
 */
export function useDrones(initialDroneId = null) {
    const [drones, setDrones] = useState([]);
    const [selectedDroneId, setSelectedDroneId] = useState(initialDroneId);
    const selectedDroneIdRef = useRef(selectedDroneId);

    // Keep ref in sync without triggering re-memoization
    useEffect(() => {
        selectedDroneIdRef.current = selectedDroneId;
    }, [selectedDroneId]);

    // Load drones from API — no dependency on selectedDroneId to avoid infinite loop
    const loadDrones = useCallback(async () => {
        try {
            const res = await apiFetch(`${API_URL}/drones`);
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    const newDrones = applySavedDroneOrder(data.drones || []);
                    // Only update state if data actually changed to prevent unnecessary re-renders
                    setDrones(prev => {
                        if (dronesEqual(prev, newDrones)) return prev;
                        return newDrones;
                    });
                    // Auto-select first connected drone if none selected
                    if (!selectedDroneIdRef.current && newDrones.length > 0) {
                        const connectedDrone = newDrones.find(d => d.status === 'connected');
                        if (connectedDrone) {
                            setSelectedDroneId(connectedDrone.id);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error loading drones:', error);
        }
    }, []);

    // Load drones on mount and periodically
    // A drone added, renamed or removed on another screen shows up now, not at the next poll.
    useEffect(() => {
        const onChange = (e) => { if (e.detail && e.detail.entity === 'drone') loadDrones(); };
        window.addEventListener('utms:entity-changed', onChange);
        return () => window.removeEventListener('utms:entity-changed', onChange);
    }, [loadDrones]);

    useEffect(() => {
        loadDrones();
        const interval = setInterval(loadDrones, 5000);
        return () => clearInterval(interval);
    }, [loadDrones]);

    // Select a drone
    const selectDrone = useCallback((droneId) => {
        setSelectedDroneId(droneId);
    }, []);

    // Get selected drone object
    const getSelectedDrone = useCallback(() => {
        return drones.find(d => d.id === selectedDroneId) || null;
    }, [drones, selectedDroneId]);

    // Reorder drones and persist to localStorage
    const reorderDrones = useCallback((newOrderedDrones) => {
        setDrones(newOrderedDrones);
        try {
            const ids = newOrderedDrones.map(d => d.id);
            localStorage.setItem(DRONE_ORDER_KEY, JSON.stringify(ids));
        } catch (e) {
            console.warn('Failed to save drone order', e);
        }
    }, []);

    return {
        drones,
        setDrones,
        reorderDrones,
        selectedDroneId,
        setSelectedDroneId: selectDrone,
        loadDrones,
        getSelectedDrone,
    };
}

export default useDrones;
